import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelToken} from '../../../helpers/asyncCancelHelpers';
import {mapToSignatureIds} from '../../../helpers/models/signatureHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {ApiTokenModel} from '../../../models/api/apiTokenModel';
import {CustomFieldResourceTypesEnum} from '../../../models/api/customFieldModel';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {SearchTagsModel} from '../../../models/api/searchTagModel';
import {SettingsRootPagesEnum} from '../../../models/url/settingsUrlModel';
import {HttpError} from '../../../services/http/httpError';
import {findPanelSequence} from '../../../services/panels';
import {batchStore} from '../../appBatching';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {createApiTokenAsyncAction, fetchApiTokensAsyncAction} from '../../data/apiTokens/apiTokensActions';
import {
  deleteCustomFieldsAsyncAction,
  fetchCustomFieldsForResourceTypeAsyncAction,
} from '../../data/customFields/customFieldsActions';
import {updateDataAction} from '../../data/dataGroupsActions';
import {
  createLocalApplicationAsyncAction,
  fetchLocalApplicationAsyncAction,
  fetchLocalApplicationsAsyncAction,
} from '../../data/localApplications/localApplicationsActions';
import {fetchSignaturesForNamespaceAsyncAction} from '../../data/signatures/signaturesActions';
import {setPanelAction} from '../panelsActions';
import {getPanels} from '../panelsSelectors';
import {PanelTypesEnum, SettingsPanelId} from '../panelStateBase';
import {getSettingsPanel} from './settingsPanelSelectors';
import {isSettingsResourcesDevelopersResource, SettingsPanelResourceTypesEnum} from './settingsPanelState';

/*
 * Constants.
 */

export enum SettingsPanelActionsEnum {
  OPEN_CUSTOM_FIELDS = '@@panels/settings/OPEN_CUSTOM_FIELDS',
  OPEN_RULES = '@@panels/settings/OPEN_RULES',
  OPEN_TAGS = '@@panels/settings/OPEN_TAGS',
  OPEN_TEAMMATE_ROOT = '@@panels/settings/OPEN_TEAMMATE_ROOT',
  OPEN_SIGNATURES = '@@panels/settings/OPEN_SIGNATURES',
  OPEN_PREFERENCES = '@@panels/settings/OPEN_PREFERENCES',
  OPEN_DEVELOPERS = '@@panels/settings/OPEN_DEVELOPERS',
  CLOSE_DEVELOPERS = '@@panels/settings/CLOSE_DEVELOPERS',

  TAGS_FETCHED = 'TAGS_FETCHED',
  FETCH_CUSTOM_FIELDS = '@@panels/settings/FETCH_CUSTOM_FIELDS',
  FETCH_CUSTOM_FIELDS_FAILED = '@@panels/settings/FETCH_CUSTOM_FIELDS_FAILED',
  FETCH_CUSTOM_FIELDS_COMPLETED = '@@panels/settings/FETCH_CUSTOM_FIELDS_COMPLETED',
  FETCH_SIGNATURES = '@@panels/settings/FETCH_SIGNATURES',
  FETCH_SIGNATURES_COMPLETED = '@@panels/settings/FETCH_SIGNATURES_COMPLETED',
  FETCH_SIGNATURES_FAILED = '@@panels/settings/FETCH_SIGNATURES_FAILED',
  FETCH_LOCAL_APPLICATIONS = '@@panels/settings/FETCH_LOCAL_APPLICATIONS',
  FETCH_LOCAL_APPLICATIONS_FAILED = '@@panels/settings/FETCH_LOCAL_APPLICATIONS_FAILED',
  FETCH_LOCAL_APPLICATIONS_COMPLETED = '@@panels/settings/FETCH_LOCAL_APPLICATIONS_COMPLETED',

  FETCH_API_TOKENS = '@@panels/settings/FETCH_API_TOKENS',
  FETCH_API_TOKENS_FAILED = '@@panels/settings/FETCH_API_TOKENS_FAILED',
  FETCH_API_TOKENS_COMPLETED = '@@panels/settings/FETCH_API_TOKENS_COMPLETED',
  API_TOKEN_CREATED = '@@panels/settings/API_TOKEN_CREATED',
  FETCH_LOCAL_APPLICATION = '@@panels/settings/FETCH_LOCAL_APPLICATION',
  FETCH_LOCAL_APPLICATION_COMPLETED = '@@panels/settings/FETCH_LOCAL_APPLICATION_COMPLETED',
  FETCH_LOCAL_APPLICATION_FAILED = '@@panels/settings/FETCH_LOCAL_APPLICATION_FAILED',
  LOCAL_APPLICATION_CREATED = '@@panels/settings/LOCAL_APPLICATION_CREATED',

  ADD_SELECTED_CUSTOM_FIELD_IDS = '@@panels/settings/ADD_SELECTED_CUSTOM_FIELD_IDS',
  REMOVE_SELECTED_CUSTOM_FIELD_IDS = '@@panels/settings/REMOVE_SELECTED_CUSTOM_FIELD_IDS',
  REMOVE_CUSTOM_FIELD_IDS = '@@panels/settings/REMOVE_CUSTOM_FIELD_IDS',
}

/*
 * Types.
 */

interface SettingsPanelActionBasePayload {
  panelId: SettingsPanelId;
}

export interface OpenSettingsCustomFieldsAction {
  type: SettingsPanelActionsEnum.OPEN_CUSTOM_FIELDS;
}

export interface OpenSettingsRulesAction {
  type: SettingsPanelActionsEnum.OPEN_RULES;
}

export interface OpenSettingsTeammateRootAction {
  type: SettingsPanelActionsEnum.OPEN_TEAMMATE_ROOT;
}

export interface OpenSettingsTagsAction {
  type: SettingsPanelActionsEnum.OPEN_TAGS;
}

export interface OpenSettingsSignaturesAction {
  type: SettingsPanelActionsEnum.OPEN_SIGNATURES;
}

export interface OpenSettingsPreferencesAction {
  type: SettingsPanelActionsEnum.OPEN_PREFERENCES;
}

export interface OpenSettingsDevelopersAction {
  type: SettingsPanelActionsEnum.OPEN_DEVELOPERS;
}

export interface CloseSettingsDevelopersAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.CLOSE_DEVELOPERS;
}

interface SettingsTagsListUpdatedPayload extends SettingsPanelActionBasePayload {
  searchTags: SearchTagsModel;
}

export interface SettingsTagsAddToListedTagsAction extends ActionWithPayload<SettingsTagsListUpdatedPayload> {
  type: SettingsPanelActionsEnum.TAGS_FETCHED;
}

export interface FetchCustomFieldsAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS;
}

interface FetchCustomFieldsCompletedActionPayload extends SettingsPanelActionBasePayload {
  customFieldIds: ReadonlyArray<number>;
}

export interface FetchCustomFieldsCompletedAction
  extends ActionWithPayload<FetchCustomFieldsCompletedActionPayload> {
  type: SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS_COMPLETED;
}

export interface FetchCustomFieldsFailedAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS_FAILED;
}

interface AddSelectedCustomFieldIdsActionPayload extends SettingsPanelActionBasePayload {
  customFieldIds: ReadonlyArray<number>;
}

export interface AddSelectedCustomFieldIdsAction
  extends ActionWithPayload<AddSelectedCustomFieldIdsActionPayload> {
  type: SettingsPanelActionsEnum.ADD_SELECTED_CUSTOM_FIELD_IDS;
}

interface RemoveSelectedCustomFieldIdsActionPayload extends SettingsPanelActionBasePayload {
  customFieldIds: ReadonlyArray<number>;
}

export interface RemoveSelectedCustomFieldIdsAction
  extends ActionWithPayload<RemoveSelectedCustomFieldIdsActionPayload> {
  type: SettingsPanelActionsEnum.REMOVE_SELECTED_CUSTOM_FIELD_IDS;
}

interface RemoveCustomFieldIdsActionPayload extends SettingsPanelActionBasePayload {
  customFieldIds: ReadonlyArray<number>;
}

export interface RemoveCustomFieldIdsAction extends ActionWithPayload<RemoveCustomFieldIdsActionPayload> {
  type: SettingsPanelActionsEnum.REMOVE_CUSTOM_FIELD_IDS;
}

export interface FetchSignaturesAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_SIGNATURES;
}

interface FetchSignaturesCompletedActionPayload extends SettingsPanelActionBasePayload {
  signatureIds: ReadonlyArray<number>;
}

export interface FetchSignaturesCompletedAction
  extends ActionWithPayload<FetchSignaturesCompletedActionPayload> {
  type: SettingsPanelActionsEnum.FETCH_SIGNATURES_COMPLETED;
}

export interface FetchSignaturesFailedAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_SIGNATURES_FAILED;
}

interface FetchLocalApplicationsActionPayload extends SettingsPanelActionBasePayload {
  shouldClearExisting?: boolean;
}

export interface FetchLocalApplicationsAction extends ActionWithPayload<FetchLocalApplicationsActionPayload> {
  type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS;
}

interface FetchLocalApplicationsCompletedActionPayload extends SettingsPanelActionBasePayload {
  localApplicationIds: ReadonlyArray<number>;
}

export interface FetchLocalApplicationsCompletedAction
  extends ActionWithPayload<FetchLocalApplicationsCompletedActionPayload> {
  type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS_COMPLETED;
}

export interface FetchLocalApplicationsFailedAction
  extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS_FAILED;
}

export interface FetchApiTokensAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_API_TOKENS;
}

interface FetchApiTokensCompletedActionPayload extends SettingsPanelActionBasePayload {
  apiTokenIds: ReadonlyArray<number>;
}

export interface FetchApiTokensCompletedAction
  extends ActionWithPayload<FetchApiTokensCompletedActionPayload> {
  type: SettingsPanelActionsEnum.FETCH_API_TOKENS_COMPLETED;
}

export interface FetchApiTokensFailedAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_API_TOKENS_FAILED;
}

interface ApiTokensCreatedActionPayload extends SettingsPanelActionBasePayload {
  apiTokenId: number;
}

export interface ApiTokensCreatedAction extends ActionWithPayload<ApiTokensCreatedActionPayload> {
  type: SettingsPanelActionsEnum.API_TOKEN_CREATED;
}

interface FetchLocalApplicationActionPayload extends SettingsPanelActionBasePayload {
  localApplicationId: number;
}
export interface FetchLocalApplicationAction extends ActionWithPayload<FetchLocalApplicationActionPayload> {
  type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION;
}

export interface FetchLocalApplicationCompletedAction
  extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION_COMPLETED;
}

export interface FetchLocalApplicationFailedAction extends ActionWithPayload<SettingsPanelActionBasePayload> {
  type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION_FAILED;
}

interface LocalApplicationCreatedActionPayload extends SettingsPanelActionBasePayload {
  localApplicationId: number;
}

export interface LocalApplicationCreatedAction
  extends ActionWithPayload<LocalApplicationCreatedActionPayload> {
  type: SettingsPanelActionsEnum.LOCAL_APPLICATION_CREATED;
}

/*
 * Action creators.
 */

// TODO: Add settings panel ID parameter.
export function openSettingsCustomFieldsAction(): OpenSettingsCustomFieldsAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_CUSTOM_FIELDS,
  };
}

// TODO: Add settings panel ID parameter.
export function openSettingsRulesAction(): OpenSettingsRulesAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_RULES,
  };
}

// TODO: Add settings panel ID parameter.
export function openSettingsTeammateRootAction(): OpenSettingsTeammateRootAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_TEAMMATE_ROOT,
  };
}

// TODO: Add settings panel ID parameter.
export function openSettingsTagsAction(): OpenSettingsTagsAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_TAGS,
  };
}

// TODO: Add settings panel ID parameter.
export function openSettingsSignaturesAction(): OpenSettingsSignaturesAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_SIGNATURES,
  };
}

// TODO: Add settings panel ID parameter.
export function openSettingsPreferencesAction(): OpenSettingsPreferencesAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_PREFERENCES,
  };
}

// TODO: Add settings panel ID parameter.
export function openDevelopersAction(): OpenSettingsDevelopersAction {
  return {
    type: SettingsPanelActionsEnum.OPEN_DEVELOPERS,
  };
}

export function closeSettingsDevelopersAction(panelId: SettingsPanelId): CloseSettingsDevelopersAction {
  return {
    type: SettingsPanelActionsEnum.CLOSE_DEVELOPERS,
    payload: {
      panelId,
    },
  };
}

export function addToSettingsTagsList(
  panelId: SettingsPanelId,
  searchTags: SearchTagsModel,
): SettingsTagsAddToListedTagsAction {
  return {
    type: SettingsPanelActionsEnum.TAGS_FETCHED,
    payload: {
      panelId,
      searchTags,
    },
  };
}

export function fetchCustomFieldsAction(panelId: SettingsPanelId): FetchCustomFieldsAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS,
    payload: {
      panelId,
    },
  };
}

export function fetchCustomFieldsCompletedAction(
  panelId: SettingsPanelId,
  customFieldIds: ReadonlyArray<number>,
): FetchCustomFieldsCompletedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS_COMPLETED,
    payload: {
      panelId,
      customFieldIds,
    },
  };
}

export function fetchCustomFieldsFailedAction(panelId: SettingsPanelId): FetchCustomFieldsFailedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_CUSTOM_FIELDS_FAILED,
    payload: {
      panelId,
    },
  };
}

export function addSelectedCustomFieldIdsAction(
  panelId: SettingsPanelId,
  customFieldIds: ReadonlyArray<number>,
): AddSelectedCustomFieldIdsAction {
  return {
    type: SettingsPanelActionsEnum.ADD_SELECTED_CUSTOM_FIELD_IDS,
    payload: {
      panelId,
      customFieldIds,
    },
  };
}

export function removeSelectedCustomFieldIdsActionPayload(
  panelId: SettingsPanelId,
  customFieldIds: ReadonlyArray<number>,
): RemoveSelectedCustomFieldIdsAction {
  return {
    type: SettingsPanelActionsEnum.REMOVE_SELECTED_CUSTOM_FIELD_IDS,
    payload: {
      panelId,
      customFieldIds,
    },
  };
}

export function removeCustomFieldIdsAction(
  panelId: SettingsPanelId,
  customFieldIds: ReadonlyArray<number>,
): RemoveCustomFieldIdsAction {
  return {
    type: SettingsPanelActionsEnum.REMOVE_CUSTOM_FIELD_IDS,
    payload: {
      panelId,
      customFieldIds,
    },
  };
}

export function fetchSignaturesAction(panelId: SettingsPanelId): FetchSignaturesAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_SIGNATURES,
    payload: {
      panelId,
    },
  };
}

export function fetchSignaturesCompletedAction(
  panelId: SettingsPanelId,
  signatureIds: ReadonlyArray<number>,
): FetchSignaturesCompletedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_SIGNATURES_COMPLETED,
    payload: {
      panelId,
      signatureIds,
    },
  };
}

export function fetchSignaturesFailedAction(panelId: SettingsPanelId): FetchSignaturesFailedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_SIGNATURES_FAILED,
    payload: {
      panelId,
    },
  };
}

export function fetchLocalApplicationsAction(
  panelId: SettingsPanelId,
  shouldClearExisting?: boolean,
): FetchLocalApplicationsAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS,
    payload: {
      panelId,
      shouldClearExisting,
    },
  };
}

export function fetchLocalApplicationsCompletedAction(
  panelId: SettingsPanelId,
  localApplicationIds: ReadonlyArray<number>,
): FetchLocalApplicationsCompletedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS_COMPLETED,
    payload: {
      panelId,
      localApplicationIds,
    },
  };
}

export function fetchLocalApplicationsFailedAction(
  panelId: SettingsPanelId,
): FetchLocalApplicationsFailedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATIONS_FAILED,
    payload: {
      panelId,
    },
  };
}

export function fetchApiTokensAction(panelId: SettingsPanelId): FetchApiTokensAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_API_TOKENS,
    payload: {
      panelId,
    },
  };
}

export function fetchApiTokensCompletedAction(
  panelId: SettingsPanelId,
  apiTokenIds: ReadonlyArray<number>,
): FetchApiTokensCompletedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_API_TOKENS_COMPLETED,
    payload: {
      panelId,
      apiTokenIds,
    },
  };
}

export function fetchApiTokensFailedAction(panelId: SettingsPanelId): FetchApiTokensFailedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_API_TOKENS_FAILED,
    payload: {
      panelId,
    },
  };
}

export function apiTokenCreatedAction(panelId: SettingsPanelId, apiTokenId: number): ApiTokensCreatedAction {
  return {
    type: SettingsPanelActionsEnum.API_TOKEN_CREATED,
    payload: {
      panelId,
      apiTokenId,
    },
  };
}

export function fetchLocalApplicationAction(
  panelId: SettingsPanelId,
  localApplicationId: number,
): FetchLocalApplicationAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION,
    payload: {
      panelId,
      localApplicationId,
    },
  };
}

export function fetchLocalApplicationCompletedAction(
  panelId: SettingsPanelId,
): FetchLocalApplicationCompletedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION_COMPLETED,
    payload: {
      panelId,
    },
  };
}

export function fetchLocalApplicationFailedAction(
  panelId: SettingsPanelId,
): FetchLocalApplicationFailedAction {
  return {
    type: SettingsPanelActionsEnum.FETCH_LOCAL_APPLICATION_FAILED,
    payload: {
      panelId,
    },
  };
}

export function localApplicationCreatedAction(
  panelId: SettingsPanelId,
  localApplicationId: number,
): LocalApplicationCreatedAction {
  return {
    type: SettingsPanelActionsEnum.LOCAL_APPLICATION_CREATED,
    payload: {
      panelId,
      localApplicationId,
    },
  };
}

/*
 * Actions.
 */

export function navigateSettingsAction(panelId: SettingsPanelId): ThunkAction {
  return (dispatch, getState) => {
    // Find the settings panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.SETTINGS) {
      throw new Error('Wrong panel type.');
    }

    // If the panel exists, we're done.
    if (panel) {
      return;
    }

    dispatch(
      setPanelAction({
        id: panelId,
        type: PanelTypesEnum.SETTINGS,
        sequence: findPanelSequence(),
        resources: {
          resourceType: SettingsPanelResourceTypesEnum.NONE,
        },
      }),
    );
  };
}

// TODO: Add required settings panel ID parameter.
export function openSettingsPageAction(page: SettingsRootPagesEnum, panelId?: SettingsPanelId): ThunkAction {
  return (dispatch, getState) => {
    const settingsPanel = getSettingsPanel(getState());
    if (!settingsPanel) {
      return;
    }

    switch (page) {
      case SettingsRootPagesEnum.TAGS: {
        dispatch(openSettingsTagsAction());
        break;
      }
      case SettingsRootPagesEnum.RULES: {
        dispatch(openSettingsRulesAction());
        break;
      }
      case SettingsRootPagesEnum.TEAMMATES: {
        dispatch(openSettingsTeammateRootAction());
        break;
      }
      case SettingsRootPagesEnum.CUSTOM_FIELDS: {
        dispatch(openSettingsCustomFieldsAction());
        break;
      }
      case SettingsRootPagesEnum.SIGNATURES: {
        dispatch(openSettingsSignaturesAction());
        break;
      }
      case SettingsRootPagesEnum.USER_PREFERENCES: {
        dispatch(openSettingsPreferencesAction());
        break;
      }
      case SettingsRootPagesEnum.DEVELOPERS: {
        dispatch(openDevelopersAction());
        break;
      }
      default:
      // Do not navigate anywhere.
    }
  };
}

// TODO: Add settings panel ID parameter.
export function fetchCustomFieldsForSettingsTableAsyncAction(
  type: CustomFieldResourceTypesEnum,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const settingsPanel = getSettingsPanel(getState());
    if (!settingsPanel) {
      return;
    }

    try {
      dispatch(fetchCustomFieldsAction(settingsPanel.id));
      const customFieldIds = await dispatch(fetchCustomFieldsForResourceTypeAsyncAction(type));
      dispatch(fetchCustomFieldsCompletedAction(settingsPanel.id, customFieldIds));
    } catch (err) {
      dispatch(fetchCustomFieldsFailedAction(settingsPanel.id));
    }
  };
}

// TODO: Add settings panel ID parameter.
export function fetchSignaturesForSettingsTableAsyncAction(namespace: string): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const settingsPanel = getSettingsPanel(getState());
    if (!settingsPanel) {
      return;
    }

    try {
      dispatch(fetchSignaturesAction(settingsPanel.id));
      const signatures = await dispatch(fetchSignaturesForNamespaceAsyncAction(namespace));
      dispatch(fetchSignaturesCompletedAction(settingsPanel.id, mapToSignatureIds(signatures)));
    } catch (err) {
      dispatch(fetchSignaturesFailedAction(settingsPanel.id));
    }
  };
}

// TODO: Add settings panel ID parameter.
export function settingsAddSelectedCustomFieldIdsAction(customFieldIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const settingsPanel = getSettingsPanel(getState());
    if (!settingsPanel) {
      return;
    }

    dispatch(addSelectedCustomFieldIdsAction(settingsPanel.id, customFieldIds));
  };
}

// TODO: Add settings panel ID parameter.
export function settingsRemoveSelectedCustomFieldIdsAction(
  customFieldIds: ReadonlyArray<number>,
): ThunkAction {
  return (dispatch, getState) => {
    const settingsPanel = getSettingsPanel(getState());
    if (!settingsPanel) {
      return;
    }

    dispatch(removeSelectedCustomFieldIdsActionPayload(settingsPanel.id, customFieldIds));
  };
}

// TODO: Add settings panel ID parameter.
export function settingsDeleteCustomFieldsAction(customFieldIds: ReadonlyArray<number>): ThunkAction {
  return (dispatch, getState) => {
    const settingsPanel = getSettingsPanel(getState());
    if (!settingsPanel) {
      return;
    }

    dispatch(removeCustomFieldIdsAction(settingsPanel.id, customFieldIds));
    dispatch(deleteCustomFieldsAsyncAction(customFieldIds));
  };
}

export function fetchLocalApplicationsForSettingsAsyncAction(
  panelId: SettingsPanelId,
  cancelToken?: CancelToken,
  shouldClearExisting: boolean = true,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the settings panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.SETTINGS) {
      throw new Error('Wrong panel type.');
    }
    if (!panel) {
      return;
    }

    try {
      const offset = isSettingsResourcesDevelopersResource(panel.resources)
        ? (panel.resources.localApplicationIds ?? []).length
        : 0;

      dispatch(fetchLocalApplicationsAction(panelId, shouldClearExisting));
      // Offset based on what we currently have in the store.
      const response = await dispatch(fetchLocalApplicationsAsyncAction({offset, cancelToken}));

      // Update the store and add references at the same time so GC doesn't clean things up.
      batchStore(() => {
        dispatch(updateDataAction({localApplications: response.items, teammates: response.teammates}));
        dispatch(
          fetchLocalApplicationsCompletedAction(
            panelId,
            response.items.map((l) => l.id),
          ),
        );
      });
    } catch (err) {
      dispatch(fetchLocalApplicationsFailedAction(panelId));
    }
  };
}

export function fetchApiTokensForSettingsAsyncAction(
  panelId: SettingsPanelId,
  cancelToken?: CancelToken,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the settings panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.SETTINGS) {
      throw new Error('Wrong panel type.');
    }
    if (!panel) {
      return;
    }

    try {
      dispatch(fetchApiTokensAction(panelId));
      const apiTokens = await dispatch(fetchApiTokensAsyncAction(cancelToken));

      // Update the store and add references at the same time so GC doesn't clean things up.
      batchStore(() => {
        dispatch(updateDataAction({apiTokens}));
        dispatch(
          fetchApiTokensCompletedAction(
            panelId,
            apiTokens.map((t) => t.id),
          ),
        );
      });
    } catch (err) {
      dispatch(fetchApiTokensFailedAction(panelId));
    }
  };
}

export function fetchLocalApplicationForSettingsAsyncAction(
  panelId: SettingsPanelId,
  localApplicationId: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    // Find the settings panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.SETTINGS) {
      throw new Error('Wrong panel type.');
    }
    if (!panel) {
      return;
    }

    dispatch(fetchLocalApplicationAction(panelId, localApplicationId));

    try {
      const response = await dispatch(fetchLocalApplicationAsyncAction(localApplicationId));

      // Update the store and add references at the same time so GC doesn't clean things up.
      batchStore(() => {
        dispatch(
          updateDataAction({
            localApplications: [response.application],
            teammates: response.creator ? [response.creator] : [],
          }),
        );
        dispatch(fetchLocalApplicationCompletedAction(panelId));
      });
    } catch {
      dispatch(fetchLocalApplicationFailedAction(panelId));
    }
  };
}

export function createApiTokensForSettingsAsyncAction(
  panelId: SettingsPanelId,
  name: string,
  scopes: ReadonlyArray<string>,
): ThunkAsyncAction<ApiTokenModel | undefined> {
  return async (dispatch, getState) => {
    // Find the settings panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.SETTINGS) {
      throw new Error('Wrong panel type.');
    }
    if (!panel) {
      return undefined;
    }

    try {
      const apiToken = await dispatch(createApiTokenAsyncAction(name, scopes));
      if (!apiToken) {
        return undefined;
      }

      // Update the store and add references at the same time so GC doesn't clean things up.
      batchStore(() => {
        dispatch(updateDataAction({apiTokens: [apiToken]}));
        dispatch(apiTokenCreatedAction(panelId, apiToken.id));
      });
      return apiToken;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

export function createLocalApplicationForSettingsAsyncAction(
  panelId: SettingsPanelId,
  localApplicationDetails: Pick<LocalApplicationModel, 'name' | 'description'>,
): ThunkAsyncAction<LocalApplicationModel | undefined> {
  return async (dispatch, getState) => {
    // Find the settings panel with this ID.
    const panel = getPanels(getState())[panelId];
    if (panel && panel.type !== PanelTypesEnum.SETTINGS) {
      throw new Error('Wrong panel type.');
    }
    if (!panel) {
      return undefined;
    }

    try {
      const localApplication = await dispatch(createLocalApplicationAsyncAction(localApplicationDetails));
      if (!localApplication) {
        return undefined;
      }

      // Update the store and add references at the same time so GC doesn't clean things up.
      batchStore(() => {
        dispatch(updateDataAction({localApplications: [localApplication]}));
        dispatch(localApplicationCreatedAction(panelId, localApplication.id));
      });
      return localApplication;
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      return undefined;
    }
  };
}

/*
 * Union type.
 */

export type SettingsPanelAction =
  | SettingsTagsAddToListedTagsAction
  | FetchCustomFieldsAction
  | FetchCustomFieldsCompletedAction
  | FetchCustomFieldsFailedAction
  | AddSelectedCustomFieldIdsAction
  | RemoveSelectedCustomFieldIdsAction
  | RemoveCustomFieldIdsAction
  | FetchSignaturesAction
  | FetchSignaturesCompletedAction
  | FetchSignaturesFailedAction
  | OpenSettingsCustomFieldsAction
  | OpenSettingsRulesAction
  | OpenSettingsTagsAction
  | OpenSettingsTeammateRootAction
  | OpenSettingsSignaturesAction
  | OpenSettingsPreferencesAction
  | OpenSettingsDevelopersAction
  | FetchLocalApplicationsAction
  | FetchLocalApplicationsCompletedAction
  | FetchLocalApplicationsFailedAction
  | FetchApiTokensAction
  | FetchApiTokensCompletedAction
  | FetchApiTokensFailedAction
  | CloseSettingsDevelopersAction
  | ApiTokensCreatedAction
  | FetchLocalApplicationAction
  | FetchLocalApplicationCompletedAction
  | FetchLocalApplicationFailedAction
  | LocalApplicationCreatedAction;
